import { useEffect, useState } from "react";
import { Cookie, X } from "lucide-react";

const KEY = "ecogest_cookie_consent_v1";

/**
 * CookieConsent — bannière CNIL/RGPD-compliant avec gestion fine.
 * Stocke le consentement dans localStorage. Les cookies analytics ne sont
 * jamais posés sans consentement explicite.
 */
export const CookieConsent = () => {
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false });

  useEffect(() => {
    if (!localStorage.getItem(KEY)) setOpen(true);
  }, []);

  const save = (allowAnalytics) => {
    localStorage.setItem(KEY, JSON.stringify({ analytics: allowAnalytics, ts: Date.now() }));
    // gtag.js is already loaded from index.html with consent default = "denied".
    // We push a consent-update signal so Google upgrades or keeps denied,
    // then immediately fire an explicit event so GA registers the new state
    // (otherwise Google may flag the session as "consent refused" until the next page).
    if (typeof window.gtag === "function") {
      window.gtag("consent", "update", {
        analytics_storage: allowAnalytics ? "granted" : "denied",
      });
      // Explicit signal — ensures Consent Mode v2 records the choice immediately
      window.gtag("event", "consent_update", {
        consent_decision: allowAnalytics ? "accepted" : "refused",
        method: "cookie_banner",
      });
      // Fire a fresh page_view in the new consent state so GA gets a "granted"
      // ping right away when the user accepts (otherwise the next ping is only on nav)
      if (allowAnalytics) {
        window.gtag("event", "page_view", {
          page_path: window.location.pathname + window.location.search,
          page_title: document.title,
        });
      }
    }
    setOpen(false);
    // Reload-like dispatch so Google Maps iframe and any consent-gated content appears
    if (allowAnalytics) {
      setTimeout(() => window.dispatchEvent(new Event("ecogest-consent-updated")), 50);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="cookie-banner"
      role="dialog"
      aria-label="Consentement cookies"
      className="fixed bottom-4 left-4 right-4 md:left-6 md:right-auto md:max-w-md z-[60] bg-white border border-gray-300 shadow-2xl shadow-black/15 p-5 md:p-6"
    >
      <div className="flex items-start gap-3 mb-3">
        <Cookie className="w-5 h-5 text-[#E60000] mt-0.5 shrink-0" strokeWidth={1.5} />
        <div className="flex-1">
          <p className="font-display font-bold tracking-tight">Cookies & vie privée</p>
        </div>
        <button onClick={() => save(false)} aria-label="Refuser et fermer" className="text-gray-400 hover:text-black -mt-1 -mr-1">
          <X className="w-4 h-4" />
        </button>
      </div>
      <p className="text-xs text-gray-600 leading-relaxed mb-4">
        Ce site utilise uniquement les cookies strictement nécessaires à son fonctionnement.
        Aucun cookie de mesure d'audience ou de pub n'est posé sans votre accord explicite.
        En savoir plus dans notre <a href="/confidentialite" className="underline hover:text-[#E60000]">politique de confidentialité</a>.
      </p>
      <div className="flex flex-wrap gap-2">
        <button
          data-testid="cookie-accept-all"
          onClick={() => save(true)}
          className="flex-1 bg-[#E60000] hover:bg-[#B30000] text-white px-4 py-2.5 text-xs font-semibold tracking-wider uppercase transition-colors"
        >
          Tout accepter
        </button>
        <button
          data-testid="cookie-reject-all"
          onClick={() => save(false)}
          className="flex-1 border border-gray-300 hover:border-black text-gray-800 px-4 py-2.5 text-xs font-semibold tracking-wider uppercase transition-colors"
        >
          Refuser
        </button>
      </div>
    </div>
  );
};

export default CookieConsent;
